import React, { useState, useEffect, useContext, useRef } from 'react';
import { useAppDispatch } from '../../app/hooks';

// event-type imports.
import { Event, EventType, EventTarget, PositionAndFOV } from '../../utils/events';
import { handleEvent } from '../../app-actions';

//	-------------------------------------------------
//
//	C O N S T A N T S
//
//	-------------------------------------------------
const ALADIN_URL: string =
  'https://aladin.cds.unistra.fr/AladinLite/api/v3/beta/aladin.js';
// populate a list of HiPS to be displayed in Aladin.
const HIPS_LIST = [
  // high energy (gamma and X-rays)
  'CDS/P/HGPS/Flux',
  'CDS/P/Fermi/5',
  'CDS/P/Fermi/4',
  'CDS/P/Fermi/3',
  'ov-gso/P/Fermi/Band2',
  'ov-gso/P/BAT/150-195keV',
  'ov-gso/P/BAT/35-50keV',
  'ov-gso/P/BAT/14-20keV',
  'erosita/dr1/rate/023',
  'erosita/dr1/rate/024',

  // uv/optical/infrared
  'CDS/P/GALEXGR6_7/FUV',
  'CDS/P/GALEXGR6_7/NUV',
  'CDS/P/DSS2/color',
  'CDS/P/PanSTARRS/DR1/g',
  'CDS/P/PanSTARRS/DR1/r',
  'CDS/P/Finkbeiner',
  'CDS/P/PanSTARRS/DR1/i',
  'CDS/P/PanSTARRS/DR1/color-i-r-g',
  'CDS/P/PanSTARRS/DR1/z',
  'CDS/P/PanSTARRS/DR1/y',
  'CDS/P/DES-DR2/ColorIRG',
  'CDS/P/2MASS/color',
  'ov-gso/P/GLIMPSE/irac1',
  'ov-gso/P/GLIMPSE/irac2',
  'CDS/P/unWISE/color-W2-W1W2-W1',
  'ov-gso/P/GLIMPSE/irac3',
  'ov-gso/P/GLIMPSE/irac4',
  'CDS/P/IRIS/color',
  'ESAVO/P/AKARI/N60',
  'ESAVO/P/AKARI/WideL',
  'ESAVO/P/HERSCHEL/SPIRE-250',
  'ESAVO/P/HERSCHEL/SPIRE-350',
  'ESAVO/P/HERSCHEL/SPIRE-500',

  // sub-mm/mm/radio
  'CDS/P/PLANCK/R3/HFI/color',
  'CDS/P/ACT_Planck/DR5/f220',
  'CDS/P/CO',
  'CDS/P/PLANCK/R3/HFI100',
  'CDS/P/PLANCK/R3/LFI30',
  'CDS/P/NVSS',
  'CSIRO/P/RACS/mid/I',
  'ov-gso/P/CGPS/VGPS',
  'CDS/C/HI4PI/HI',
  'CDS/P/MeerKAT/Galactic-Centre-1284MHz-StokesI',
  'CSIRO/P/RACS/low/I',
  'astron.nl/P/tgssadr',
  'ov-gso/P/GLEAM/170-231',
  'ov-gso/P/GLEAM/139-170',
  'astron.nl/P/lotss_dr2_high',
  'ov-gso/P/GLEAM/103-134',
  'ov-gso/P/GLEAM/072-103',
  // New HiPS3D
  'https://alasky.cds.unistra.fr/HIPS3D/GalfaHI/',
  'https://alasky.cds.unistra.fr/HIPS3D/SKADC2/',
  'https://alasky.cds.unistra.fr/HIPS3D/MUSE-test/',
  'https://alasky.cds.unistra.fr/HIPS3D/LGLBSHI16/',
  'https://alasky.cds.unistra.fr/HIPS3D/DHIGLS/',
  'https://alasky.cds.unistra.fr/HIPS3D/CALIFA-DR3/CDS_C_CALIFA_DR3_COMB/',
  'https://alasky.cds.unistra.fr/HIPS3D/HI4PI/CDS_C_HI4PI_NHI/',
  'https://alasky.cds.unistra.fr/HIPS3D/SEDIGISM/CDS_C_SEDIGISM_13CO/',
];

//	------------------------------------------------------------
//
//	Add the SKAO Rucio catalog as the requested position.
//
//	------------------------------------------------------------

export function AddCatalog(props: {
  aladin: any;
  ra: number;
  dec: number;
  selectedIDs: string[];
  setSelectedIDs?: any;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  var win: any = window;

  // remove any existing layers.
  props.aladin.removeLayers();

  // add the SKAO Rucio catalog.
  win.A.catalogFromSKAORucio(
    `${props.ra.toString()} ${props.dec.toString()}`,
    90,
    { onClick: 'showTable' },
    (cat: any) => {
      cat.setHoverColor('#ffffff');
      props.aladin.addCatalog(cat);

      if (props.setSelectedIDs !== undefined) {
        const selected: { id: string; ra: number; dec: number }[] = [];
        if (props.selectedIDs.length > 0) {
          for (let i = 0; i < cat.sources.length; i++) {
            for (let j = 0; j < props.selectedIDs.length; j++) {
              if (cat.sources[i].data.obs_publisher_did === props.selectedIDs[j]) {
                cat.sources[i].actionClicked();
                selected.push({
                  id: cat.sources[i].data.obs_publisher_did,
                  ra: cat.sources[i].ra,
                  dec: cat.sources[i].dec,
                });
              }
            }
          }
        }
        dispatch(props.setSelectedIDs(selected));
      }
    },
  );
  //
} // AddCatalog

//	------------------------------------------------------------
//
//	Move the Aladin view to another location, and maybe
//	reload the catalog.
//
//	------------------------------------------------------------

export function moveAladinView(props: {
  aladin: any;
  ra: number;
  dec: number;
  fov: number;
  selectedIDs: string[];
  setSelectedIDs: any;
  loadCatalog: boolean;
}) {
  //
  // update the position of the view.
  props.aladin.setFov(props.fov);
  props.aladin.gotoRaDec(props.ra, props.dec);
  // add the SKAO Rucio catalog.
  if (props.loadCatalog) {
    AddCatalog(props);
  }
  //
} // moveAladinView

//	------------------------------------------------------------
//
//	Place an Aladin Lite component on the page
//
//	Optional parameters:
//
//		[selectedIDs, setSelectedIDs] - a list of selected footprints/sources
//		highlightedID - the ID of a footprint/source that should be hovered
//		setAladinRefresh - number state. will be updated to a random value
//					to force the page to rerender.
//
//	------------------------------------------------------------

export function Aladin(props: {
  divElementID: string;
  aladin: any;
  setAladin: any;
  selectedIDs?:
    | {
        id: string;
        ra: number;
        dec: number;
      }[]
    | undefined;
  setSelectedIDs?: any;
  highlightedID?: string | undefined;
  setHighlightedID?: any;
  setAladinRefresh?: (n: any) => void;
  setLoadingComponent: any;
  updateSearchCatalogPosition?: boolean;
  running: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  //	-------------------------------------------------
  //
  //	state variables
  //
  //	-------------------------------------------------

  // hold position and field-of-view
  const [sPosition, setPosition] = useState<{ ra: number; dec: number }>({
    ra: 0.0,
    dec: 0.0,
  });
  const [sFOV, setFOV] = useState<number>(5.0);
  // Keep refs to detach listeners / destroy cleanly
  const listenersAdded = useRef(false);

  //	-------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	-------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Check if a particular script has already been added to the
  //	page.
  //
  //	------------------------------------------------------------
  function isScriptOnPage(url: string) {
    return Array.from(document.getElementsByTagName('script')).some((s) => s.src === url);
  }

  function loadScript(url: string) {
    return new Promise<void>((resolve, reject) => {
      const el = document.createElement('script');
      el.src = url;
      el.async = true;
      el.charset = 'utf-8';
      el.onload = () => resolve();
      el.onerror = reject;
      document.body.appendChild(el);
    });
  }

  function destroyAladin() {
    try {
      if (props.aladin) {
        // remove event listeners
        // (Aladin doesn't expose removeListener API for every event; clearing the instance is usually fine)
        props.aladin.removeLayers && props.aladin.removeLayers();
        const container = document.getElementById(props.divElementID);
        if (container) container.innerHTML = '';
      }
    } catch {}
    listenersAdded.current = false;
    props.setAladin(null);
  }

  //	------------------------------------------------------------
  //
  //	Initialise Aladin Lite.
  //
  //	------------------------------------------------------------

  async function initAladin() {
    //
    const win: any = window;
    if (!isScriptOnPage(ALADIN_URL)) {
      await loadScript(ALADIN_URL);
    }

    // mark loading done
    dispatch(props.setLoadingComponent(false));

    await win.A.init;
    const aladin = win.A.aladin('#' + props.divElementID, {
      fov: 5.0,
      target: '0 0',
      projection: 'AIT',
      showContextMenu: true,
      hipsList: HIPS_LIST,
    });

    dispatch(props.setAladin(aladin));

    if (!listenersAdded.current) {
      if (props.setSelectedIDs !== undefined) {
        aladin.on('objectClicked', (object: any) => {
          //
          const selected: {
            id: string;
            ra: number;
            dec: number;
          }[] = [];
          if (object) {
            if ('data' in object) {
              selected.push({
                id: object.data.obs_publisher_did,
                ra: object.ra,
                dec: object.dec,
              });
            } else if ('source' in object && 'data' in object.source) {
              selected.push({
                id: object.source.data.obs_publisher_did,
                ra: object.source.ra,
                dec: object.source.dec,
              });
            }
          }
          if (props.setSelectedIDs !== undefined) {
            dispatch(props.setSelectedIDs(selected));
          }
          //
        });

        aladin.on('select', (object: any) => {
          const selected: any[] = [];
          if (object && object.length > 0) {
            const sources: any = object[0];
            for (let i = 0; i < sources.length; i++) {
              if ('data' in sources[i]) {
                selected.push({
                  id: sources[i].data.obs_publisher_did,
                  ra: sources[i].ra,
                  dec: sources[i].dec,
                });
              } else if ('source' in sources[i] && 'data' in sources[i].source) {
                selected.push({
                  id: sources[i].source.data.obs_publisher_did,
                  ra: sources[i].source.ra,
                  dec: sources[i].source.dec,
                });
              }
            }
          }
          dispatch(props.setSelectedIDs?.(selected));
        });
      }

      if (props.setHighlightedID !== undefined) {
        aladin.on('objectHovered', (object: any) => {
          if (object) {
            if ('data' in object) {
              dispatch(props.setHighlightedID?.(object.data.obs_publisher_did));
            } else if ('source' in object && 'data' in object.source) {
              dispatch(props.setHighlightedID?.(object.source.data.obs_publisher_did));
            }
          } else {
            dispatch(props.setHighlightedID?.(''));
          }
        });
        aladin.on('objectHoveredStop', () => dispatch(props.setHighlightedID('')));
      }

      if (props.setAladinRefresh) {
        aladin.on('click', () => props.setAladinRefresh?.(Math.random()));
      }

      aladin.on('positionChanged', (o: any) => setPosition({ ra: o.ra, dec: o.dec }));
      aladin.on('zoomChanged', (fov: number) => setFOV(fov));

      listenersAdded.current = true;
    }

    // initial catalog
    AddCatalog({
      aladin,
      ra: 0,
      dec: 0,
      selectedIDs: [],
      setSelectedIDs: props.setSelectedIDs,
    });
    //
  } // initAladin

  //	------------------------------------------------------------
  //
  //	Check if the script has already been added to the page, and
  //	if not add it.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    if (!props.running) return () => {};

    let disposed = false;
    (async () => {
      try {
        await initAladin();
      } catch (e) {
        if (!disposed) console.error(e);
      }
    })();

    return () => {
      disposed = true;
      destroyAladin();
    };
    // Re‑init if the container id changes, or when running flips
  }, [props.running, props.divElementID]);

  //	------------------------------------------------------------
  //
  //	Initialise Aladin Lite.
  //
  //	------------------------------------------------------------

  function startAladin(args: {
    ra: number;
    dec: number;
    fov: number;
    selectedIDs: string[];
    highlightedID: string;
    loadCatalog: boolean;
  }) {
    const win: any = window;

    // v3 of the API.
    let aladin: any;
    win.A.init.then(() => {
      try {
        aladin = win.A.aladin('#' + props.divElementID, {
          fov: args.fov,
          target: args.ra.toString() + ' ' + args.dec.toString(),
          projection: 'AIT',
          showContextMenu: true,
          hipsList: HIPS_LIST,
        });

        // store the Aladin session in state.
        dispatch(props.setAladin(aladin));

        // add the on-click event to sources, so the selected object state is updated when something is clicked.
        if (props.setSelectedIDs !== undefined)
          aladin.on('objectClicked', function (object: any) {
            var selectedIDs: { id: string; ra: number; dec: number }[] = [];

            if (object) {
              if ('data' in object)
                selectedIDs.push({
                  id: object.data.obs_publisher_did,
                  ra: object.ra,
                  dec: object.dec,
                });
              else if ('source' in object)
                if ('data' in object.source)
                  selectedIDs.push({
                    id: object.source.data.obs_publisher_did,
                    ra: object.source.ra,
                    dec: object.source.dec,
                  });
            }

            // update selected IDs.
            dispatch(props.setSelectedIDs?.(selectedIDs));
          });

        // add the hovered event to sources, so the highlighted-object state is updated when something is hovered over.
        if (props.setHighlightedID !== undefined)
          aladin.on('objectHovered', function (object: any) {
            if (object) {
              // get the obs_publisher_id, either from object.data, or if it's a footprint
              // then get it from .source.data
              if ('data' in object) {
                dispatch(props.setHighlightedID?.(object.data.obs_publisher_did));
              } else if ('source' in object)
                if ('data' in object.source) {
                  dispatch(
                    props.setHighlightedID?.(object.source.data.obs_publisher_did),
                  );
                }
            } else {
              dispatch(props.setHighlightedID?.(''));
            }
          });

        // add the hovered stop event to sources, so the highlighted-object state is reset when something is no longer hovered over.
        if (props.setHighlightedID !== undefined) {
          aladin.on('objectHoveredStop', function (object: any) {
            dispatch(props.setHighlightedID(''));
          });
        }

        // add the select event so that if multiple objects are selected then the state is updated.
        if (props.setSelectedIDs !== undefined)
          aladin.on('select', function (object: any) {
            // build a list of selected items.
            var selectedIDs: { id: string; ra: number; dec: number }[] = [];

            if (object)
              if (object.length > 0) {
                const sources: any = object[0];
                for (var i = 0; i < sources.length; i++) {
                  if ('data' in sources[i])
                    selectedIDs.push({
                      id: sources[i].data.obs_publisher_did,
                      ra: sources[i].ra,
                      dec: sources[i].dec,
                    });
                  else if ('source' in sources[i])
                    if ('data' in sources[i].source)
                      selectedIDs.push({
                        id: sources[i].source.data.obs_publisher_did,
                        ra: sources[i].source.ra,
                        dec: sources[i].source.dec,
                      });
                }
              }

            // update selected IDs.
            dispatch(props.setSelectedIDs?.(selectedIDs));
          });

        // add the click event so that the state is updated to trigger a refresh when Aladin is clicked. we need to do
        // this so that we can clear the selection in state when the selection is cleared in Aladin.
        if (props.setAladinRefresh !== undefined)
          aladin.on('click', function (object: any) {
            props.setAladinRefresh?.(Math.random());
          });

        // check for the position of the Aladin view changing.
        aladin.on('positionChanged', function (object: any) {
          // build the new position.
          const newPosition: { ra: number; dec: number } = {
            ra: object.ra,
            dec: object.dec,
          };

          // update the state.
          setPosition(newPosition);
        });

        // check for the zoom of the Aladin view changing.
        aladin.on('zoomChanged', function (object: any) {
          // update the state.
          setFOV(object);
        });

        console.log('sAladin:');
        console.log(aladin);

        // add the SKAO Rucio catalog.
        // AddCatalog({
        //   aladin: aladin,
        //   ra: args.ra,
        //   dec: args.dec,
        //   selectedIDs: args.selectedIDs,
        //   setSelectedIDs?: props.setSelectedIDs,
        // });
      } catch {}
    });
  } // startAladin

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  if (
    props.aladin &&
    props.running &&
    props.aladin.view?.catalogs?.length > 0 &&
    props.highlightedID !== undefined
  ) {
    for (let c = 0; c < props.aladin.view.catalogs.length; c++) {
      const cat: any = props.aladin.view.catalogs[c];
      for (let i = 0; i < cat.sources.length; i++) {
        if (
          cat.sources[i].data.obs_publisher_did === props.highlightedID &&
          props.highlightedID !== ''
        ) {
          cat.sources[i].hover();
        } else {
          cat.sources[i].unhover();
        }
      }
      for (let i = 0; i < (cat.footprints?.length ?? 0); i++) {
        if (
          cat.footprints[i].source.data.obs_publisher_did === props.highlightedID &&
          props.highlightedID !== ''
        ) {
          cat.footprints[i].hover();
        } else {
          cat.footprints[i].unhover();
        }
      }
    }
  }

  //	------------------------------------------------------------
  //
  //	Kick off some processes once the page has loaded.
  //
  //	------------------------------------------------------------

  // keep selection state in sync with external clears
  useEffect(() => {
    if (!props.aladin || !props.selectedIDs || !props.setSelectedIDs) return;
    if ('selection' in props.aladin.view) {
      if (props.aladin.view.selection === null && props.selectedIDs.length > 0) {
        dispatch(props.setSelectedIDs([]));
      }
    }
  }, [props.aladin, props.selectedIDs, props.setSelectedIDs]);

  useEffect(() => {
    if (!props.updateSearchCatalogPosition) return;

    const payload: PositionAndFOV = {
      position: sPosition,
      fov: sFOV,
    };

    const newEvent: Event = {
      eventTarget: EventTarget.SEARCH_CATALOG_FORM,
      eventType: EventType.UPDATE_SEARCH_RA_DEC,
      parameters: payload,
    };

    dispatch(handleEvent({ event: newEvent }));
  }, [props.updateSearchCatalogPosition, sPosition, sFOV, dispatch]);

  return (
    <div
      id={props.divElementID}
      style={{
        flex: '1 1',
        display: props.running ? 'flex' : 'none',
        width: '100%',
      }}
    />
  );
} // Aladin
